import environment from '../environment';
import { getAuth } from 'firebase/auth';

const ACCESS_TOKEN_KEY = 'ecg_tkn';
const ACCESS_TOKEN_EXPIRY_KEY = 'ecg_tkn_exp';
const apiUrl = environment.apiUrl;

interface RequestOptions {
  headers?: Record<string, string>;
  body?: any;
  isFormData?: boolean;
}

class ApiClient {
  private token: string | null = null;
  private tokenExpiry: number | null = null;

  constructor() {
    this.loadToken();
  }

  private loadToken() {
    this.token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const expiry = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    this.tokenExpiry = expiry ? parseInt(expiry, 10) : null;
  }

  private saveToken(token: string, expiresIn: number) {
    this.token = token;
    this.tokenExpiry = Date.now() + expiresIn * 1000; // expiresIn is in seconds
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, this.tokenExpiry.toString());
  }

  private async getAuthToken(): Promise<string | null> {
    const auth = getAuth();
    if (auth.currentUser) {
      const idTokenResult = await auth.currentUser.getIdTokenResult(true); // force refresh
      if (idTokenResult.token) {
        this.saveToken(
          idTokenResult.token,
          idTokenResult.expirationTime
            ? (new Date(idTokenResult.expirationTime).getTime() - Date.now()) / 1000
            : 3600,
        );
        return idTokenResult.token;
      }
    }
    return null;
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    options?: RequestOptions,
  ): Promise<T> {
    let currentToken = this.token;

    // Refresh token if expired or not present
    if (!currentToken || (this.tokenExpiry && Date.now() > this.tokenExpiry)) {
      currentToken = await this.getAuthToken();
    }

    const headers: Record<string, string> = {
      ...(options?.headers || {}),
    };

    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    if (!options?.isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      method,
      headers,
      body: options?.body
        ? options?.isFormData
          ? options.body
          : JSON.stringify(options.body)
        : undefined,
    };

    try {
      const response = await fetch(`${apiUrl}${path}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const message = errorData.message;
        throw new Error(
          Array.isArray(message)
            ? message.join(', ')
            : message || 'An unknown error occurred',
        );
      }

      const text = await response.text();
      if (!text) {
        return undefined as T;
      }

      return JSON.parse(text) as T;
    } catch (error) {
      console.error('API Request Failed:', error);
      throw error;
    }
  }

  get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>('GET', path, { headers });
  }

  post<T>(
    path: string,
    body: any,
    headers?: Record<string, string>,
    isFormData?: boolean,
  ): Promise<T> {
    return this.request<T>('POST', path, { body, headers, isFormData });
  }

  put<T>(path: string, body: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>('PUT', path, { body, headers });
  }

  patch<T>(path: string, body: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>('PATCH', path, { body, headers });
  }

  delete<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>('DELETE', path, { headers });
  }
}

export const apiService = new ApiClient();
